/**
 * CihFaqSection — numbered reference-style FAQ rows. Each row is a native
 * `<details>` whose summary toggles the answer below it.
 *
 * Every answer is server-rendered and collapsed by the browser, not by React
 * state: the pages emit FAQPage JSON-LD, and crawlers only credit answers
 * that are in the HTML. The shared `name` makes the rows an exclusive
 * accordion, and find-in-page opens a collapsed row that holds a match.
 *
 * Aliases: faq section, faqs, frequently asked
 */

import { useId } from "react";

import { CihSectionHeader } from "~/components/cih/section-header";

type Faq = { readonly question: string; readonly answer: string };

type Props = {
  readonly faqs: ReadonlyArray<Faq>;
  readonly sectionLabel?: string;
  readonly heading?: string;
};

export function CihFaqSection({
  faqs = [],
  sectionLabel = "Frequently asked",
  heading = "Questions",
}: Props) {
  const groupName = useId();

  if (faqs.length === 0) return null;

  return (
    <>
      <CihSectionHeader label={sectionLabel} title={heading} />
      <div className="border-t border-gray-200">
        {faqs.map((faq, i) => (
          <details
            key={`${i}-${faq.question}`}
            name={groupName}
            open={i === 0}
            className="group border-b border-gray-200"
          >
            <summary className="cursor-pointer list-none transition-colors hover:bg-gray-50 [&::-webkit-details-marker]:hidden">
              <div className="grid grid-cols-[72px_1fr_auto] items-stretch gap-4 text-left md:grid-cols-[180px_1fr_auto] md:gap-10">
                <div className="self-stretch border-r border-gray-200 py-6 pr-4 pl-4 text-xs font-medium tracking-[0.22em] text-gray-400 uppercase md:py-7 md:pl-14">
                  Q.{String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="self-center py-6 text-base leading-snug font-medium tracking-tight text-gray-900 md:py-7 md:text-lg">
                  {faq.question}
                </h3>
                <div
                  aria-hidden="true"
                  className="self-center py-6 pr-4 text-lg text-gray-400 md:py-7 md:pr-14"
                >
                  {/* Rotate only the glyph, not the padded cell — the cell's
                      box sits flush at the (full-bleed) viewport edge, so
                      rotating it would push its bounding box off-screen and
                      create a horizontal scrollbar. */}
                  <span className="inline-block transition-transform group-open:rotate-45">
                    +
                  </span>
                </div>
              </div>
            </summary>
            <div className="grid grid-cols-[72px_1fr_auto] gap-4 md:grid-cols-[180px_1fr_auto] md:gap-10">
              <div className="self-stretch border-r border-gray-200" />
              <p className="max-w-2xl py-1 pb-7 text-sm leading-relaxed text-gray-700 md:text-base">
                {faq.answer}
              </p>
              <div />
            </div>
          </details>
        ))}
      </div>
    </>
  );
}
